// KPI viz — renders a panel result as a row of summary cards.
//
// Shape detection (in priority order):
//
//   1. panel.kpiKeys present + result has 2+ columns:
//      treat result as label/value rows (SHOW METRICS shape) and pick
//      the rows whose first-column label matches a key in kpiKeys.
//      One card per matched key, in the order kpiKeys is given.
//
//   2. Single-row result with multiple columns:
//      one card per column (column name = label, cell = value).
//
//   3. Multi-row result with 2 columns:
//      one card per row (col 0 = label, col 1 = value), capped at 8.
//
//   4. Anything else falls back to the empty placeholder — KPI viz
//      doesn't know how to summarize this, the report editor should
//      pick a different viz.

function fmtValue(v) {
  if (v == null) return '—';
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return String(v);
    if (v >= 1_000_000_000) return (v / 1_000_000_000).toFixed(1) + 'B';
    if (v >= 1_000_000)     return (v / 1_000_000).toFixed(1) + 'M';
    if (v >= 1_000)         return (v / 1_000).toFixed(1) + 'k';
    return v.toLocaleString();
  }
  // Numeric strings (SHOW METRICS gives "106.15") — coerce.
  if (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v)) {
    return fmtValue(parseFloat(v));
  }
  return String(v);
}

function Card({ label, value }) {
  return (
    <div className="kpi-card">
      <div className="label">{label}</div>
      <div className="value">{fmtValue(value)}</div>
    </div>
  );
}

export function KpiViz({ columns, rows, error, loading, panel }) {
  if (error) return <div className="report-viz-empty error">{error}</div>;
  if (loading && !rows) return <div className="report-viz-empty">Loading…</div>;
  if (!rows?.length) return <div className="report-viz-empty">No data</div>;

  const cards = pickCards(columns, rows, panel);
  if (!cards.length) {
    return <div className="report-viz-empty">KPI viz couldn't summarize these columns. Try table viz.</div>;
  }
  return (
    <div className="kpi-strip">
      {cards.map((c, i) => <Card key={c.label + '|' + i} label={c.label} value={c.value} />)}
    </div>
  );
}

function pickCards(columns, rows, panel) {
  // Path 1: explicit kpiKeys + label/value shape.
  const keys = Array.isArray(panel?.kpiKeys) ? panel.kpiKeys : null;
  if (keys && columns?.length >= 2) {
    const byLabel = new Map();
    for (const r of rows) byLabel.set(r[0], r[1]);
    const out = [];
    for (const k of keys) {
      if (byLabel.has(k)) out.push({ label: k, value: byLabel.get(k) });
    }
    if (out.length) return out;
  }

  // Path 2: single row × N cols → one card per column.
  if (rows.length === 1 && columns?.length > 0) {
    return columns.map((col, i) => ({ label: col, value: rows[0][i] }));
  }

  // Path 3: multi-row, exactly 2 cols → one card per row (capped).
  if (columns?.length === 2 && rows.length <= 8) {
    return rows.map(r => ({ label: String(r[0]), value: r[1] }));
  }

  return [];
}
